import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { BarList, Card, RangePicker, Spinner } from '@/components';
import type { BreakdownsResponse, Range, RetentionResponse } from '@/lib/admin-types';
import { formatInt } from '@/lib/format';
import { apiJson } from '@/lib/api';

type CohortSearch = { range: Range };

const COHORTS_HINTS = {
  retention:
    'Each row is devices whose first_seen falls in that ISO week (Monday, Pacific). Column W0 is the cohort size; W1+ shows what share still had last_seen activity in later ISO weeks. Darker cells mean more of the cohort was active that week.',
  platform:
    'Registered devices grouped by the platform string saved at registration/heartbeat (e.g. ios, android). Counts are all-time.',
  appVersion:
    'Devices grouped by client-reported app_version. Long tails roll into “Other”.',
  locale:
    'Devices grouped by client-reported locale/language tag.',
  osVersion:
    'Devices grouped by client-reported OS version string.',
} as const;

function cellOpacity(cohortSize: number, count: number, weekIdx: number) {
  if (cohortSize <= 0) return 0;
  if (weekIdx === 0) return 0.35;
  const pct = Math.min(1, count / cohortSize);
  return 0.08 + pct * 0.55;
}

function CohortsPage() {
  const { range } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/cohorts' });

  const breakdownQ = useQuery({
    queryKey: ['admin', 'breakdowns'],
    queryFn: () => apiJson<BreakdownsResponse>('/v1/admin/analytics/breakdowns'),
  });

  const retentionQ = useQuery({
    queryKey: ['admin', 'retention', 4],
    queryFn: () => apiJson<RetentionResponse>('/v1/admin/analytics/retention?weeks=4'),
  });

  const loading = breakdownQ.isLoading || retentionQ.isLoading;
  const err = breakdownQ.error || retentionQ.error;

  const ret = retentionQ.data;

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Cohorts</h1>
          <p className="mt-1 text-sm text-gray-500">
            Weekly retention (Pacific weeks) and device breakdowns. Range controls future charts; breakdowns
            are all-time.
          </p>
        </div>
        <RangePicker
          value={range}
          onChange={(r) => void navigate({ search: { range: r } })}
        />
      </div>

      {err && (
        <p className="text-sm text-red-600">
          {(err as Error).message}
          <button type="button" className="ml-2 underline" onClick={() => void breakdownQ.refetch()}>
            Retry
          </button>
        </p>
      )}

      <Card padding="md" className="cursor-help" title={COHORTS_HINTS.retention}>
        <h2 className="mb-3 text-sm font-semibold text-gray-800">Weekly retention</h2>
        {retentionQ.isLoading && (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        )}
        {ret && ret.rows.length === 0 && (
          <p className="text-sm text-gray-500">No cohorts yet — register devices to see retention.</p>
        )}
        {ret && ret.rows.length > 0 && (
          <div className="max-w-full overflow-x-auto">
            <table className="w-full min-w-[28rem] border-collapse text-sm">
              <thead>
                <tr>
                  <th
                    className="cursor-help border border-gray-200 bg-gray-50 px-2 py-2 text-left text-xs font-medium text-gray-600"
                    title="ISO week (Monday) when these devices first registered."
                  >
                    Cohort (ISO week)
                  </th>
                  <th
                    className="cursor-help border border-gray-200 bg-gray-50 px-2 py-2 text-right text-xs font-medium text-gray-600"
                    title="Number of devices whose first_seen falls in this cohort week."
                  >
                    Size
                  </th>
                  {Array.from({ length: ret.weeks }, (_, i) => (
                    <th
                      key={i}
                      className="cursor-help border border-gray-200 bg-gray-50 px-2 py-2 text-center text-xs font-medium text-gray-600"
                      title={
                        i === 0
                          ? 'Cohort size (100% baseline)—everyone registered this week.'
                          : `Week +${i}: percent of the cohort whose last_seen falls in that ISO week (offset from cohort Monday).`
                      }
                    >
                      W{i}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ret.rows.map((row) => (
                  <tr key={row.cohort_week}>
                    <td className="border border-gray-200 px-2 py-1 font-mono text-xs text-gray-700">
                      {row.cohort_week}
                    </td>
                    <td className="border border-gray-200 px-2 py-1 text-right tabular-nums text-gray-800">
                      {formatInt(row.cohort_size)}
                    </td>
                    {row.retained.map((c, i) => {
                      const pct =
                        row.cohort_size > 0 ? Math.round((c / row.cohort_size) * 100) : 0;
                      const op = cellOpacity(row.cohort_size, c, i);
                      return (
                        <td
                          key={i}
                          className="border border-gray-200 px-1 py-1 text-center text-xs tabular-nums text-gray-900"
                          style={{ backgroundColor: `rgba(15,138,138,${op})` }}
                          title={`${formatInt(c)} devices (${pct}%)`}
                        >
                          {pct}%
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card padding="md" className="cursor-help" title={COHORTS_HINTS.platform}>
          <h2 className="mb-3 text-sm font-semibold text-gray-800">By platform</h2>
          {loading ? <div className="h-40 animate-pulse rounded-xl bg-gray-200/60" /> : <BarList data={breakdownQ.data?.by_platform ?? []} />}
        </Card>
        <Card padding="md" className="cursor-help" title={COHORTS_HINTS.appVersion}>
          <h2 className="mb-3 text-sm font-semibold text-gray-800">By app version</h2>
          {loading ? <div className="h-40 animate-pulse rounded-xl bg-gray-200/60" /> : <BarList data={breakdownQ.data?.by_app_version ?? []} />}
        </Card>
        <Card padding="md" className="cursor-help" title={COHORTS_HINTS.locale}>
          <h2 className="mb-3 text-sm font-semibold text-gray-800">By locale</h2>
          {loading ? <div className="h-40 animate-pulse rounded-xl bg-gray-200/60" /> : <BarList data={breakdownQ.data?.by_locale ?? []} />}
        </Card>
        <Card padding="md" className="cursor-help" title={COHORTS_HINTS.osVersion}>
          <h2 className="mb-3 text-sm font-semibold text-gray-800">By OS version</h2>
          {loading ? <div className="h-40 animate-pulse rounded-xl bg-gray-200/60" /> : <BarList data={breakdownQ.data?.by_os_version ?? []} />}
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/cohorts')({
  validateSearch: (raw: Record<string, unknown>): CohortSearch => {
    const r = raw.range;
    if (r === '30d' || r === '90d' || r === '7d') return { range: r };
    return { range: '7d' };
  },
  component: CohortsPage,
});
